<template>
  <Transition name="modal-fade">
    <div v-if="show" class="subscription-expiration-modal">
      <div class="modal-overlay">
        <div class="modal-content">
          <div class="modal-header">
            <div class="header-content">
              <div class="warning-icon">
                <el-icon size="32">
                  <WarningFilled />
                </el-icon>
              </div>
              <div class="header-text">
                <h2>{{ modalTitle }}</h2>
                <p>{{ modalDescription }}</p>
              </div>
            </div>
          </div>
          
          <div class="modal-body">
            <!-- Subscription View Content -->
            <SubscriptionView />
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { WarningFilled } from '@element-plus/icons-vue'
import SubscriptionView from '@/views/subscription/SubscriptionView.vue'
import { useSubscriptionStore } from '@/stores/subscription'
import { useLanguageStore } from '@/stores/language'

const languageStore = useLanguageStore()
const { t } = languageStore

const props = defineProps<{
  show: boolean
}>()

const subscriptionStore = useSubscriptionStore()

const modalTitle = computed(() => {
  const hasUnpaidOrders = (subscriptionStore.subscriptionData?.unpaid_orders_count || 0) > 0
  return hasUnpaidOrders 
    ? t('subscription.expirationModal.unpaidInvoicesTitle') 
    : t('subscription.expirationModal.expiredTitle')
})

const modalDescription = computed(() => {
  const hasUnpaidOrders = (subscriptionStore.subscriptionData?.unpaid_orders_count || 0) > 0
  const isCloud = subscriptionStore.subscriptionData?.package?.is_cloud ?? true
  const hasDealer = !!subscriptionStore.subscriptionData?.company?.dealer
  
  if (hasUnpaidOrders) {
    // Different message for non-cloud companies with dealer
    if (!isCloud && hasDealer) {
      return t('subscription.expirationModal.unpaidDealerDescription')
    }
    return t('subscription.expirationModal.unpaidDescription')
  }
  return t('subscription.expirationModal.expiredDescription')
})
</script>

<style scoped>
.subscription-expiration-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
}

.modal-content {
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  width: 95%;
  max-width: 1200px;
  max-height: 95vh;
  display: flex;
  flex-direction: column;
}

.modal-header {
  background: linear-gradient(135deg, #f56c6c 0%, #e74c3c 100%);
  color: white;
  padding: 20px 24px;
  flex-shrink: 0;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 20px;
}

.warning-icon {
  flex-shrink: 0;
  color: white !important;
  background-color: #f56c6c !important;
}

.header-text h2 {
  margin: 0 0 8px 0;
  font-size: 24px;
  font-weight: 600;
}

.header-text p {
  margin: 0;
  font-size: 16px;
  opacity: 0.9;
  line-height: 1.5;
}

.modal-body {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* Modal fade transition */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

/* Responsive design */
@media (max-width: 768px) {
  .modal-content {
    width: 98%;
    margin: 10px;
    max-height: 98vh;
  }
  
  .header-content {
    flex-direction: column;
    text-align: center;
    gap: 16px;
  }
  
  .header-text h2 {
    font-size: 20px;
  }
  
  .header-text p {
    font-size: 14px;
  }
}
</style> 